/**
 * Every string `/plans` renders.
 *
 * Two rules shape what is here and what is not. **The scope is stated, never
 * asked** — every plan opens the catalogue in full for its whole period, and
 * that is a platform setting rather than a plan decision, so it appears twice as
 * prose (above the table and at the foot of the dialog) and nowhere as a
 * control. And **catalog reasoning stays out of the interface**: the operator
 * needs to know that a plan opens everything, not that a configuration row says
 * so.
 *
 * The currency word appears in no string here. It travels on the row, written
 * from the `CURRENCY` setting when the plan was created, and is rendered through
 * the shared money formatter at the cell.
 */
export const copy = {
  crumb: 'الخطط',

  head: {
    title: 'خطط الاشتراك',
    lede: 'ما تعرّفه هنا هو ما يراه المشترك في صفحة اختيار الخطّة. لكلّ خطّة اسمٌ وسعرٌ ومدّة، وما تحصّله كلّ واحدة يظهر في تقارير الاشتراك.',
  },

  section: {
    heading: 'الخطط المعرَّفة',
    add: 'أضف خطّة',
  },

  /** Above the table, not beneath it: it says what the table manages before it is read. */
  contextLead:
    'كلّ خطّة تفتح الفهرس كاملاً — كلّ الأفلام والمسلسلات — طوال مدّتها، بثّاً وبكلّ الجودات حتّى ‎4K‎.',
  contextRest: 'الفرق بين خطّة وأخرى المدّة والسعر لا غير، فلا خطّة تفتح أقلّ من غيرها.',

  caption: 'خطط الاشتراك المعرَّفة، بسعر كلّ خطّة وفترتها',

  columns: {
    plan: 'الخطّة',
    price: 'السعر',
    period: 'الفترة',
    actions: 'أفعال الخطّة',
  },

  /** The line under a plan's name: what happens when its period runs out. */
  renewalNote: 'تنتهي الخطّة بانتهاء فترتها، ولا تُجدَّد تلقائياً',

  rowMenu: {
    label: (name: string) => `أفعال خطّة «${name}»`,
    edit: 'حرّر الخطّة',
    delete: 'احذف الخطّة',
  },

  truncationTail: 'الأحدث أولاً.',

  /** The two routes that open the catalogue for a subscriber, named where the operator is. */
  outroLead: 'طريقان اثنان يفتحان الفهرس للمشترك: خطّة يشتريها من هنا، أو منحة تمنحها له بنفسك من',
  outroUsersLink: 'صفحة حسابه',
  outroMiddle: '. لا خطّة مجّانية ولا شراء منفرد لعنوان. والدفع يمرّ بالبوّابة الفاعلة مباشرةً —',
  outroGatewaysLink: 'إدارة بوّابات الدفع',

  dialog: {
    createTitle: 'أضف خطّة',
    editTitle: 'حرّر الخطّة',
    context: 'خطّة اشتراك',

    nameLabel: 'اسم الخطّة',

    priceLabel: 'السعر',
    /** The currency word is interpolated from the row; the sentence carries none of its own. */
    priceHint: (currency: string) =>
      `بـ${currency} — عدد صحيح بلا كسور. العملة موحّدة للمنصّة ولا تُغيَّر من هنا.`,
    /** Where no plan exists yet, no currency has been written on any row to read. */
    priceHintNoCurrency: 'عدد صحيح بلا كسور. العملة موحّدة للمنصّة ولا تُغيَّر من هنا.',

    periodLabel: 'الفترة',
    periodHint: 'بالأشهر — مدّة الاشتراك الواحد، ويتجدّد تلقائياً بانقضائها.',

    scopeLead: 'ما تفتحه هذه الخطّة ثابت ولا يُحرَّر:',
    scopeStrong: 'الفهرس كاملاً',
    scopeRest: 'طوال مدّتها — كلّ الأفلام والمسلسلات، بثّاً، بكلّ الجودات حتّى ‎4K‎.',
  },

  confirm: {
    heading: (name: string) => `حذف خطّة «${name}» نهائياً؟`,
    body: 'تختفي الخطّة من صفحة اختيار الخطّة فوراً فلا يشترك بها أحد بعد الآن. الاستحقاقات القائمة سجلّاتٌ بذاتها، فتبقى كما هي حتى انتهاء فترة كلّ منها.',
    commit: 'احذف الخطّة',
  },

  validation: {
    nameRequired: 'اكتب اسماً للخطّة.',
    nameTooLong: 'الاسم أطول ممّا يقبله السجلّ.',
    priceRequired: 'اكتب سعر الخطّة.',
    pricePositive: 'السعر عدد صحيح أكبر من صفر.',
    periodRequired: 'اكتب مدّة الاشتراك بالأشهر.',
    periodPositive: 'المدّة شهر واحد على الأقلّ.',
  },

  states: {
    loading: 'جارٍ تحميل الخطط',
    empty: 'لا خطّة معرَّفة بعد. أضف واحدة ليختارها المشترك.',
  },

  toast: {
    created: 'أُضيفت الخطّة.',
    updated: 'حُفظت الخطّة.',
    deleted: 'حُذفت الخطّة.',
  },
} as const;
